import type {CopilotChatCommandType} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import type {SelectSuggestionsEvent, ShowSuggestionsEvent, Trigger} from '@github-ui/inline-autocomplete/types'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import type {DetailedHTMLProps, InputHTMLAttributes, ReactElement} from 'react'
import {useDeferredValue, useEffect, useMemo, useRef, useState} from 'react'

import type {AvailableCommand} from '../../hooks/use-available-commands'
import {useChatStateValues} from '../../utils/CopilotChatContext'
import {useChatManager} from '../../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../../utils/reference-mention'
import styles from '../ChatInput.module.css'
import {categoryNames, CategoryValue} from './categories'
import {isEmptyStateSuggestionKey} from './EmptyStateSuggestion'
import {type AutocompleteQuery, useAutocompleteSuggestions} from './useAutocompleteSuggestions'

// Matches the filter part of an `owner/repo` string. This is the text the user types after
// the `/` to filter the repo picker.
//
// Repo names on GitHub allow alphanumerics, dots, hyphens, and underscores, so we accept
// all of those (eg `owner/docs.github.com`, `owner/react.js`).
//
// We still reject queries that contain other chars like spaces or punctuation so the picker
// closes once the user stops typing a repo name. False-positives like file paths
// (eg `owner/foo.md`) just produce an empty match list, which keeps the picker hidden.
const REPO_FILTER_PATTERN = /^[a-zA-Z0-9_.-]+$/

// Owner logins on GitHub are alphanumeric plus dashes, but we still scrub anything outside
// that set before turning an owner into a trigger. This stops a weird cached entry from ever
// producing a trigger that contains regex/whitespace surprises.
const isSafeOwnerLogin = (owner: string) => /^[a-zA-Z0-9_-]+$/.test(owner)

// Matches text that looks like the start of an `owner/` reference. We use this to decide when
// to warm the top-repos cache. The owner part must be at the start of the input or follow
// whitespace, so a slash in the middle of a word (eg a file path like `src/foo`) doesn't fire
// a fetch. Prose like `and/or` or `yes/no` still matches and triggers a fetch, but the cost
// is small: the fetch is idempotent and cheap, and the picker won't open unless the matched
// owner is one we have cached as a trigger.
const OWNER_SLASH_PATTERN = /(?:^|\s)[a-zA-Z0-9_-]+\//

/** Returns a blank query corresponding to the selected category. */
function selectedCategoryQuery(category: CategoryValue): AutocompleteQuery {
  switch (category) {
    case 'repositories':
      return {category: 'repositories', filter: ''}
    case 'repositories:discussions':
      return {category: 'repositories', filter: '', nextCategory: 'discussions'}
    case 'repositories:issues':
      return {category: 'repositories', filter: '', nextCategory: 'issues'}
    case 'repositories:pulls':
      return {category: 'repositories', filter: '', nextCategory: 'pulls'}
    case 'repositories:files':
      return {category: 'repositories', filter: '', nextCategory: 'files'}
  }
}

// null return ensures we get completeness detection from the typechecker because undefined is not allowed to be returned
function getTitleForQuery(query: AutocompleteQuery): string | null {
  switch (query.category) {
    case 'categories':
    case 'commands':
      return null
    case 'repositories':
      switch (query.nextCategory) {
        case 'discussions':
        case 'issues':
        case 'pulls':
        case 'files':
          return `${categoryNames[query.nextCategory]} ›`
        default:
          return 'Repositories ›'
      }
    case 'discussions':
    case 'issues':
    case 'pulls':
    case 'files':
      return `${categoryNames[query.category]} › ${query.repository} ›`
    case 'sso-orgs':
      return 'Single sign-on'
  }
}

export interface ChatInputAutocompleteProps {
  children: ReactElement<DetailedHTMLProps<InputHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>>
  onSelectCommand?: (command: CopilotChatCommandType) => void
  onSelectReference: (ReferenceMention: ReferenceMention) => void
  hideAutocomplete?: boolean
  availableCommands: AvailableCommand[]
  availableModes: Array<{name: string}>
  /**
   * Current text in the textarea. When provided, the autocomplete watches it for
   * `owner/` patterns and warms the top-repos cache the first time one appears. This
   * is what powers proactive `<owner>/repo` triggers without making the user open the
   * picker first.
   */
  inputValue?: string
}

/**
 * Warm up the top-repositories cache for the `<owner>/repo` autocomplete triggers. Kept at module
 * scope (rather than inline in the effect) so the try/catch doesn't prevent the React Compiler from
 * optimizing `ChatInputAutocomplete`, which re-renders on every keystroke.
 */
async function warmUpTopRepositories(
  chatManager: ReturnType<typeof useChatManager>,
  currentRepositoryId: number | undefined,
) {
  try {
    const topRepos = await chatManager.service.fetchRepositoriesSearch(undefined, undefined, false, currentRepositoryId)
    chatManager.setTopRepositoryTopics(topRepos)
  } catch {
    // Network blip - the next matching keystroke will trigger another attempt.
  }
}

export function ChatInputAutocomplete({
  children,
  onSelectCommand,
  onSelectReference,
  hideAutocomplete,
  availableCommands,
  availableModes,
  inputValue,
}: ChatInputAutocompleteProps) {
  const {mode, topRepositoriesCache, currentRepository} = useChatStateValues(
    'mode',
    'topRepositoriesCache',
    'currentRepository',
  )
  const chatManager = useChatManager()

  const currentRepositoryId = currentRepository?.id
  // Lazily fetch the top-repos cache the first time the input value looks like the start
  // of an `<owner>/repo` reference. Once the cache lands, the per-owner triggers below
  // pick up the new owners on the next render and the picker can open as the user types.
  //
  // We don't dedupe in-flight requests. The endpoint is idempotent and cheap, and the
  // window where duplicates could fire (between the first call and the cache landing in
  // state) is small enough that it doesn't matter in practice.
  useEffect(() => {
    if (topRepositoriesCache) return
    if (!inputValue || !OWNER_SLASH_PATTERN.test(inputValue)) return

    void warmUpTopRepositories(chatManager, currentRepositoryId)
  }, [inputValue, topRepositoriesCache, currentRepositoryId, chatManager])

  // Build one trigger per known owner login (eg `github/`, `vercel/`). The trigger system
  // already supports multi-character triggers, so each `<owner>/` is a normal trigger with
  // no inline-autocomplete changes required. We only register triggers for owners we have
  // cached, which keeps the picker from opening on random `foo/` text.
  //
  // This memo reruns whenever `topRepositoriesCache` changes, so once the lazy warm-up
  // populates the cache, the triggers automatically expand to include the new owners.
  const proactiveOwnerTriggers = useMemo<Trigger[]>(() => {
    if (!topRepositoriesCache) return []
    // Triggers are static strings, so case matters. If an owner is `MyOrg`, only typing
    // `MyOrg/` would open the picker. Users should not have to match the original casing,
    // so we also register a lowercase version (eg `myorg/`) when the owner has any
    // uppercase letters. Both variants point at the same picker.
    const triggerChars = new Set<string>()
    for (const repo of topRepositoriesCache) {
      const owner = repo.ownerLogin
      if (!isSafeOwnerLogin(owner)) continue
      triggerChars.add(`${owner}/`)
      const lower = owner.toLowerCase()
      if (lower !== owner) triggerChars.add(`${lower}/`)
    }
    return Array.from(triggerChars).map(triggerChar => ({
      triggerChar,
      insertSpaceOnCommit: true,
      keepTriggerCharOnCommit: false,
      multiWord: false,
    }))
  }, [topRepositoriesCache])

  const triggers: Trigger[] = [
    {triggerChar: '@', insertSpaceOnCommit: true, keepTriggerCharOnCommit: false, multiWord: true},
    ...proactiveOwnerTriggers,
  ]
  triggers.push({triggerChar: '/', insertSpaceOnCommit: true, keepTriggerCharOnCommit: false, multiWord: false})

  const {isBrowser} = useRuntimeEnvironment()

  const [query, setQuery] = useState<AutocompleteQuery | null>(null)
  // Compute suggestions from a deferred copy of the query so the per-keystroke filtering and dropdown
  // re-render happen at a lower priority — off the keystroke's critical path. The textarea commits
  // immediately; the dropdown shows the previous results for ~1 frame while the new ones compute
  // (standard search-as-you-type behavior). The selection and title logic below also read
  // `deferredQuery` to stay consistent with the displayed suggestions; only `onShowSuggestions`
  // (the keystroke handler) reads the live `query`.
  const deferredQuery = useDeferredValue(query)
  const {suggestions, stale: suggestionsDataStale} = useAutocompleteSuggestions({
    query: deferredQuery,
    availableCommands,
  })

  // Tracks whether the proactive repo trigger is currently active and whether we have already
  // emitted the "shown" telemetry event for this open session. We wait until the overlay is
  // actually visible (suggestions is a non-empty array) before emitting, so the event reflects
  // pickers users actually see.
  const [proactiveRepoActive, setProactiveRepoActive] = useState(false)
  const proactiveRepoShownRef = useRef(false)

  useEffect(() => {
    if (!proactiveRepoActive) return
    if (proactiveRepoShownRef.current) return
    if (!Array.isArray(suggestions) || suggestions.length === 0) return
    proactiveRepoShownRef.current = true
    sendEvent('dotcom_chat.activate', {
      target: 'PROACTIVE_REPO_AUTOCOMPLETE_SHOWN',
      mode,
    })
  }, [suggestions, mode, proactiveRepoActive])

  // Only apply the grid-stack wrapper class when the CSS autosize layout is enabled; the legacy JS
  // autosize path renders a bare wrapper exactly as before.
  const autocompleteWrapperClassName = copilotFeatureFlags.cssTextareaAutosize
    ? styles.inputAutocompleteWrapper
    : undefined

  const onSelectSuggestion = ({suggestion}: SelectSuggestionsEvent) => {
    // Empty-state items are non-selectable feedback ("No repositories found"). Click
    // selection is already swallowed by the row's onMouseDown handler; this guard
    // covers the telemetry / multistep transition paths so we don't emit a bogus
    // selection event or try to drill into a category from a placeholder row.
    if (typeof suggestion === 'object' && suggestion.key && isEmptyStateSuggestionKey(suggestion.key)) return

    const suggestionValue = typeof suggestion === 'string' ? suggestion : suggestion.value
    sendEvent('dotcom_chat.activate', {
      target: 'AUTOCOMPLETE_ITEM_SELECTED',
      mode,
      suggestion: suggestionValue,
    })

    const isCommand = typeof suggestion === 'object' && deferredQuery?.category === 'commands'

    if (isCommand) {
      onSelectCommand?.(suggestion.key as CopilotChatCommandType)
      return
    }

    const mention = suggestionValue !== null ? ReferenceMention.parse(suggestionValue) : undefined
    if (mention) onSelectReference(mention)

    // Reopen the menu for multistep queries
    const isMultistepSuggestion = typeof suggestion === 'object' && suggestion.value === null
    if (!isMultistepSuggestion) return

    // Hack that allows us to put the Extensions marketplace link into the menu
    const openLinkMatch = /^open-link:(.+)$/.exec(suggestion.key)
    if (openLinkMatch) {
      setQuery(null)
      window.open(openLinkMatch[1], '_blank')
      return
    }

    switch (deferredQuery?.category) {
      case 'categories':
        if (CategoryValue.is(suggestion.key)) setQuery(selectedCategoryQuery(suggestion.key))
        break
      case 'repositories':
        if (suggestion.key === 'sso-orgs') setQuery({category: 'sso-orgs', filter: ''})
        else if (deferredQuery.nextCategory) {
          const nextCategory = deferredQuery.nextCategory
          setQuery({category: nextCategory, filter: '', repository: suggestion.key})
        }
        break
    }
  }

  const onShowSuggestions = (event: ShowSuggestionsEvent) => {
    const eventTarget = event.target as HTMLTextAreaElement
    const {triggerChar} = event.trigger

    // Multi-character `<owner>/` triggers (eg `github/`) are how the proactive repo flow
    // opens the picker. The single-char `/` slash-command trigger is handled in the next
    // branch below.
    if (triggerChar.length > 1 && triggerChar.endsWith('/')) {
      // Close the picker as soon as the user types something outside the allowed filter
      // chars (eg `?` or a space, or an empty filter from a bare `github/`).
      if (!REPO_FILTER_PATTERN.test(event.query)) {
        onHideSuggestions()
        return
      }
      setProactiveRepoActive(true)
      const owner = triggerChar.slice(0, -1)
      setQuery({category: 'repositories', filter: `${owner}/${event.query}`, proactive: true})
      return
    }

    if (triggerChar === '/') {
      if (
        // not in the commands category: only trigger if the caret is at the very start
        // in the commands category: always update the filter
        (query === null && eventTarget.selectionStart === 1 && eventTarget.selectionEnd === 1) ||
        query?.category === 'commands'
      ) {
        setProactiveRepoActive(false)
        setQuery({category: 'commands', filter: event.query})
      }

      return
    }

    setProactiveRepoActive(false)
    setQuery(q =>
      q
        ? {...q, filter: event.query}
        : {
            category: 'categories',
            filter: event.query,
            index: (eventTarget.selectionStart ?? 0) - (event.query.length + event.trigger.triggerChar.length),
          },
    )
  }

  const onHideSuggestions = () => {
    setProactiveRepoActive(false)
    proactiveRepoShownRef.current = false
    setQuery(null)
  }

  const shouldReplaceText = ({suggestion}: SelectSuggestionsEvent) => {
    // Check if the command key matches any available mode name
    if (typeof suggestion === 'object' && deferredQuery?.category === 'commands' && suggestion.key !== undefined) {
      const isCommandWithMode = availableModes.some(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        availableMode => availableMode.name.toLowerCase() === suggestion.key!.toLowerCase(),
      )
      // When this command has an associated mode, don't replace text
      // (the mode change will be the action instead)
      if (isCommandWithMode) {
        return false
      }
    }
    return true
  }

  if (!isBrowser || hideAutocomplete) return <div className={autocompleteWrapperClassName}>{children}</div>

  return (
    <>
      <InlineAutocomplete
        fullWidth
        containerClassName={autocompleteWrapperClassName}
        suggestions={suggestions}
        triggers={triggers}
        tabInsertsSuggestions={false}
        onHideSuggestions={onHideSuggestions}
        onShowSuggestions={onShowSuggestions}
        onSelectSuggestion={onSelectSuggestion}
        shouldReplaceText={shouldReplaceText}
        suggestionsPlacement="below"
        title={(deferredQuery && getTitleForQuery(deferredQuery)) || undefined}
        asMenu
      >
        {children}
      </InlineAutocomplete>
      {suggestionsDataStale && (
        <span role="status" aria-live="polite" className="sr-only">
          Loading
        </span>
      )}
    </>
  )
}
